/** Helpers shared by the domain-services pages. */

import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { AddonService } from '../../api/areas/services';
import { Field } from '../../components/blocks';
import { withPolicyNames } from '../../lib/requirementNames';
import { useApi } from '../../lib/useApi';

type Lookup = (key: string, options?: Record<string, unknown>) => string;

/** A translated enum value (`services:enum.<group>.<value>`), falling back to the raw value. */
export function useEnum() {
  const { t } = useTranslation('services');
  const tr = t as unknown as Lookup;
  return (group: string, value: string | null | undefined) => (value ? tr(`enum.${group}.${value}`, { defaultValue: value }) : '—');
}

/** The Vietnamese or English name, following the interface language. */
export function useLocalName() {
  const { i18n } = useTranslation();
  return (x: { name_vi: string; name_en: string }) => (i18n.language === 'vi' ? x.name_vi : x.name_en);
}

/** A requirement document's name, a policy it cites by the policy's name (lib/requirementNames.ts). */
export function useDocName() {
  const { i18n } = useTranslation();
  const name = useLocalName();
  const tr = i18n.t.bind(i18n) as unknown as (key: string, o: { ns: string }) => string;
  const policy = (key: string) => (i18n.exists(`labels.policy.${key}`, { ns: 'common' }) ? tr(`labels.policy.${key}`, { ns: 'common' }) : undefined);
  return (d: { name_vi: string; name_en: string }) => withPolicyNames(name(d), policy);
}

// ponytail: the catalogue (config/addons.yaml) is read once per page load; it changes only with a deploy.
let catalogue: Promise<AddonService[]> | undefined;

/** The add-on catalogue, loaded once for every form of the page; empty until it is in. */
export function useAddonCatalogue(): AddonService[] {
  const addons = useApi(
    () =>
      (catalogue ??= api.listAddonCatalogue().then(
        (c) => c.addons,
        (e: unknown) => {
          catalogue = undefined;
          throw e;
        },
      )),
    [],
  );
  return addons.status === 'success' ? addons.data : [];
}

/** An add-on's catalogue name for a code the API gives alone (a price rule, a preset); the code until the catalogue is in. */
export function useAddonName() {
  const name = useLocalName();
  const addons = useAddonCatalogue();
  return (code: string) => {
    const a = addons.find((x) => x.code === code);
    return a ? name(a) : code;
  };
}

export function NumberField({ label, value, onChange, min = 0, max = 365 }: { label: string; value: number; onChange: (v: number) => void; min?: number; max?: number }) {
  return (
    <Field label={label}>
      <Input type="number" min={min} max={max} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  );
}

export function CheckField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" className="size-4" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** "30, 7" ⇄ [30, 7]. */
export const daysList = {
  parse: (s: string) =>
    s
      .split(/[,\s]+/)
      .map((x) => Number(x))
      .filter((x) => Number.isFinite(x) && x > 0),
  show: (v: number[]) => v.join(', '),
};

/** Local datetime input value → ISO instant (empty → undefined). */
export function instant(local: string): string | undefined {
  return local ? new Date(local).toISOString() : undefined;
}
